import React from 'react';
import PropTypes from 'prop-types';
import d3, { useD3 } from 'hooks/useD3';
import * as topojson from 'topojson-client';
import { scaleThreshold } from 'd3-scale';
import { schemeBlues } from 'd3-scale-chromatic';
import './index.css';
import mississippiGeoData from './ms.json';

const ChoroplethMap = ({
  data = {},
  onMouseOver = () => null,
  onMouseOut = () => null,
  onCountySelect = () => null,
  onCountyMouseOver = () => null,
  onCountyMouseOut = () => null,
  selectedCountyName = '',
  hoverColor = '#fff',
}) => {
  const ref = useD3(
    (svg) => {
      const parent = document.getElementById('ChoroplethMap');
      const width = parent.clientWidth;
      const height = parent.clientHeight;
      svg.selectAll('*').remove();
      svg.attr('width', width).attr('height', height);

      const colorScale = scaleThreshold().domain([1, 20, 50, 100, 125, 200]).range(schemeBlues[7]);

      // Format the Mississippi county Geo data
      const topology = topojson.feature(mississippiGeoData, mississippiGeoData.objects.counties);
      // Flat projection and calculate the scale and position translation
      const projection = d3.geoMercator().translate([width / 2, height / 2]);
      const bounds = d3.geoBounds(topology);
      const center = d3.geoCentroid(topology);
      const distance = d3.geoDistance(bounds[0], bounds[1]);
      const scale = height / distance / Math.sqrt(2);
      projection.scale(scale).center(center);

      // Attach our generic handlers
      svg.on('mouseover', onMouseOver);
      svg.on('mouseout', onMouseOut);

      // Create the original paths
      const paths = svg
        .append('g')
        .selectAll('path')
        .data(topology.features)
        .enter()
        .append('path');

      paths.attr('d', d3.geoPath().projection(projection));

      // Add out county click handler
      paths.on('click', function (d) {
        onCountySelect(d.properties);
      });

      // Handle our onMouseOver styling and call our callback
      paths.on('mouseover', function (d, i, a) {
        const el = d3.select(this);
        el.style('fill', hoverColor);
        onCountyMouseOver(d.properties);
        el.raise();
      });

      // Handle our onMouseOut styling and call our callback
      paths.on('mouseout', function (d) {
        const el = d3.select(this);
        el.style('fill', colorScale(d.total));
        onCountyMouseOut(d.properties);
      });

      // Set the fill color -- just highlight the county if one is selected or figure out the color from our scale
      paths.style('fill', function (d) {
        if (selectedCountyName === d.properties.name) {
          const el = d3.select(this);
          el.raise();
          return;
        } else {
          d.total = data[d.properties.countyFP] || 0;
          return colorScale(d.total);
        }
      });

      // Save us some cycles by adding this for common styling
      paths.attr('class', function (d) {
        if (selectedCountyName === d.properties.name) {
          return 'county selected';
        } else {
          return 'county';
        }
      });

      // Just for debugging purposes -- remove this
      paths.attr('name', function (d) {
        return d.properties.NAME;
      });
    },
    [data, selectedCountyName, hoverColor],
  );
  return <svg data-testid="choropleth-map_root" ref={ref} id="ChoroplethMap" />;
};

ChoroplethMap.propTypes = {
  data: PropTypes.object,
};

export default React.memo(ChoroplethMap);
